import { toPng } from 'html-to-image'
import { getNodesBounds, getViewportForBounds, MarkerType, type Edge, type Node } from '@xyflow/react'
import dayjs from 'dayjs'
import { START_NODE_ID } from '../chatbot.constants'

type TPdfLabels = {
  chatbotLabel: string
  establishmentLabel: string
  interventionLabel: string
  exportDateLabel: string
  legendLabel: string
  alertResponseLabel: string
  normalResponseLabel: string
  contentLabel: string
  pageLabel: (current: number, total: number) => string
}

type TBasePdfParams = {
  flowContainer: HTMLDivElement
  chatbotName: string
  projectName: string
  followupName: string
  labels: TPdfLabels
}

export type TPdfOrientation = 'landscape' | 'portrait'

type TPreviewPdfExportParams = TBasePdfParams & {
  nodes: Node[]
  edges: Edge[]
  applyLayout: (nodes: Node[], edges: Edge[]) => Promise<void>
  restoreLayout: () => void
  zoomOverride?: number
  orientation: TPdfOrientation
  autoLayout: boolean
}

type TGridPdfExportParams = TBasePdfParams & {
  nodes: Node[]
  edges: Edge[]
  applyLayout: (nodes: Node[], edges: Edge[]) => Promise<void>
  restoreLayout: () => void
  orientation: TPdfOrientation
}

type TJsPdfDoc = InstanceType<typeof import('jspdf').jsPDF>

const PAGE_MARGIN = 16
const HEADER_HEIGHT = 30
const FOOTER_HEIGHT = 24

const LANDSCAPE_IMAGE_WIDTH = 1600
const LANDSCAPE_IMAGE_HEIGHT = 1000
const PORTRAIT_IMAGE_WIDTH = 1000
const PORTRAIT_IMAGE_HEIGHT = 1600
const LANDSCAPE_GRID_COLS = 4
const LANDSCAPE_GRID_ROWS = 3
const PORTRAIT_GRID_COLS = 3
const PORTRAIT_GRID_ROWS = 4

const NODE_PADDING = 20
const DEFAULT_NODE_HEIGHT = 200
const DEFAULT_NODE_WIDTH = 280
const MIN_PREVIEW_ZOOM = 0.4
const MAX_PREVIEW_ZOOM = 1.5
const HIER_H_GAP = 60
const HIER_V_GAP = 80

const GRID_CELL_WIDTH = 420
const GRID_CELL_HEIGHT = 350

function getPdfDimensions(orientation: TPdfOrientation) {
  if (orientation === 'portrait') {
    return {
      imageWidth: PORTRAIT_IMAGE_WIDTH,
      imageHeight: PORTRAIT_IMAGE_HEIGHT,
      gridCols: PORTRAIT_GRID_COLS,
      gridRows: PORTRAIT_GRID_ROWS,
    }
  }
  return {
    imageWidth: LANDSCAPE_IMAGE_WIDTH,
    imageHeight: LANDSCAPE_IMAGE_HEIGHT,
    gridCols: LANDSCAPE_GRID_COLS,
    gridRows: LANDSCAPE_GRID_ROWS,
  }
}

type TInlinedEdgeStyle = { element: SVGElement; stroke: string; strokeWidth: string; fill: string }

function inlineEdgeStyles(viewportElement: HTMLElement) {
  const inlined: TInlinedEdgeStyle[] = []

  for (const path of viewportElement.querySelectorAll('.react-flow__edge-path')) {
    const svgEl = path as SVGElement
    const computed = getComputedStyle(svgEl)
    inlined.push({
      element: svgEl,
      stroke: svgEl.style.stroke,
      strokeWidth: svgEl.style.strokeWidth,
      fill: svgEl.style.fill,
    })
    svgEl.style.stroke = computed.stroke
    svgEl.style.strokeWidth = computed.strokeWidth
  }

  for (const path of viewportElement.querySelectorAll('marker path, marker polygon')) {
    const svgEl = path as SVGElement
    const computed = getComputedStyle(svgEl)
    inlined.push({
      element: svgEl,
      stroke: svgEl.style.stroke,
      strokeWidth: svgEl.style.strokeWidth,
      fill: svgEl.style.fill,
    })
    svgEl.style.fill = computed.fill
  }

  return inlined
}

function restoreEdgeStyles(inlined: TInlinedEdgeStyle[]) {
  for (const { element, stroke, strokeWidth, fill } of inlined) {
    element.style.stroke = stroke
    element.style.strokeWidth = strokeWidth
    element.style.fill = fill
  }
}

const LOGO_WIDTH = 196
const LOGO_HEIGHT = 32
// Extracted from CurecallLogoSecondaryColor in @honestica/curecall-ui — update if the logo changes
const CURECALL_LOGO_SVG = [
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${LOGO_WIDTH} ${LOGO_HEIGHT}">`,
  '<defs><linearGradient id="lg" y1="0.5" x2="1" y2="0.5" gradientUnits="objectBoundingBox">',
  '<stop offset="0" stop-color="#33c"/><stop offset="0.293" stop-color="#335ccc"/>',
  '<stop offset="0.951" stop-color="#33c4cc"/><stop offset="1" stop-color="#3cc"/>',
  '</linearGradient></defs>',
  '<path fill="#33c" transform="translate(-596.387,-6209.827)" d="M622.155,6232.157a2.687,2.687,0,0,0-1.932.816h0l-.033.036-.04.042a10.6,10.6,0,0,1-12.98,2.01l-2.569,2.57-.726-.727,0,0-2.373-2.372v0l-.727-.726.728-.728h0l1.793-1.792a10.6,10.6,0,0,1,16.291-13.243l0,0a2.7,2.7,0,1,0,3.861-3.77h0l0,0c-.045-.045-.091-.087-.139-.13a16,16,0,1,0,.75,22.644,2.7,2.7,0,0,0-1.9-4.618Z"/>',
  '<path fill="url(#lg)" transform="translate(-597.365,-6211.007)" d="M613.79,6227.006a2.7,2.7,0,1,1-2.7-2.7A2.7,2.7,0,0,1,613.79,6227.006Zm5.066-2.7a2.7,2.7,0,1,0,2.7,2.7A2.7,2.7,0,0,0,618.856,6224.307Zm7.765,0a2.7,2.7,0,1,0,2.7,2.7A2.7,2.7,0,0,0,626.621,6224.307Z"/>',
  '<path fill="url(#lg)" transform="translate(-599.785,-6210.506)" d="M647.746,6237.175a11.122,11.122,0,0,1-4.292-.789,8,8,0,0,1-3.082-2.179,9.418,9.418,0,0,1-1.707-2.984,10.463,10.463,0,0,1-.57-3.459,10.63,10.63,0,0,1,.581-3.482,9.691,9.691,0,0,1,1.742-3.068,8.244,8.244,0,0,1,3.1-2.235,10.615,10.615,0,0,1,4.229-.817,9.275,9.275,0,0,1,6.692,2.781l.583.573-3.246,3.135-.553-.544a4.7,4.7,0,0,0-3.476-1.346,4.552,4.552,0,0,0-3.691,1.45,5.518,5.518,0,0,0,0,7.03,4.577,4.577,0,0,0,3.693,1.384,5.391,5.391,0,0,0,1.967-.388,4.808,4.808,0,0,0,1.672-1.073l.566-.566,3.27,3.306-.565.56A9.451,9.451,0,0,1,647.746,6237.175Zm19.595-.024a8.367,8.367,0,0,1-5.847-2.142,7.915,7.915,0,0,1-2.371-6.124v-10.366h4.718v10.366a3.886,3.886,0,0,0,.943,2.855,3.415,3.415,0,0,0,2.582.955,3.02,3.02,0,0,0,2.411-.948,4.212,4.212,0,0,0,.874-2.862v-10.366h4.718v10.366a8.178,8.178,0,0,1-2.221,6.106A8,8,0,0,1,667.341,6237.15Zm30.224-.357h-4.9l-4.869-5.585h-1.874v5.585h-4.742v-18.322h.8q1.338,0,3.989.012t3.959.012a6.816,6.816,0,0,1,4.98,1.87,6.129,6.129,0,0,1,1.83,4.463,6.534,6.534,0,0,1-1.144,3.807,5.157,5.157,0,0,1-2.447,1.856l4.426,4.99Zm-11.647-10.017h4a2,2,0,0,0,1.551-.532,1.892,1.892,0,0,0,.517-1.345,1.871,1.871,0,0,0-.525-1.341,2.006,2.006,0,0,0-1.544-.537h-4Zm29.69,10.017h-14.1v-18.3h14.1v4.646h-9.38v2.253h9.046v4.526h-9.046v2.18h9.38Zm14.312.382a11.122,11.122,0,0,1-4.292-.789,7.984,7.984,0,0,1-3.082-2.179,9.4,9.4,0,0,1-1.707-2.984,10.445,10.445,0,0,1-.569-3.459,10.627,10.627,0,0,1,.581-3.482,9.672,9.672,0,0,1,1.741-3.068,8.244,8.244,0,0,1,3.1-2.235,10.615,10.615,0,0,1,4.229-.817,9.275,9.275,0,0,1,6.692,2.781l.583.573-3.246,3.135-.553-.544a4.7,4.7,0,0,0-3.476-1.346,4.554,4.554,0,0,0-3.691,1.45,5.52,5.52,0,0,0,0,7.03,4.578,4.578,0,0,0,3.693,1.384,5.394,5.394,0,0,0,1.967-.388,4.809,4.809,0,0,0,1.672-1.073l.566-.566,3.27,3.306-.565.56A9.452,9.452,0,0,1,729.921,6237.175Zm29.607-.382h-5.181l-1.408-3.15h-7.707l-1.432,3.15h-5.154l8.208-18.3h4.467Zm-12.329-7.678h3.776l-1.888-4.327Zm29.606,7.678H763.47v-18.3h4.741v13.795H776.8Zm18.259,0H781.729v-18.3h4.741v13.795h8.593Z"/>',
  '</svg>',
].join('')

async function getLogoDataUrl(): Promise<string | null> {
  try {
    const svgBlob = new Blob([CURECALL_LOGO_SVG], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(svgBlob)

    return await new Promise<string>((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const scale = 4
        canvas.width = LOGO_WIDTH * scale
        canvas.height = LOGO_HEIGHT * scale
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          URL.revokeObjectURL(url)
          reject(new Error('Failed to get canvas context'))
          return
        }
        ctx.scale(scale, scale)
        ctx.drawImage(img, 0, 0, LOGO_WIDTH, LOGO_HEIGHT)
        URL.revokeObjectURL(url)
        resolve(canvas.toDataURL('image/png'))
      }
      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('Failed to load logo'))
      }
      img.src = url
    })
  } catch {
    return null
  }
}

async function buildFontEmbedCSS(): Promise<string> {
  try {
    const { NEXA_REGULAR_TTF_BASE64, NEXA_BOLD_TTF_BASE64 } = await import('./chatbot-pdf-fonts')

    // Next.js localFont sets font-family directly via className on <html>,
    // not via the --font-nexa CSS variable. Read the actual computed font-family.
    const computedFontFamily = getComputedStyle(document.documentElement).fontFamily
    const fontFamily = computedFontFamily.split(',')[0]?.trim().replaceAll(/['"]/g, '')

    if (!fontFamily) return ''

    return [
      `@font-face { font-family: '${fontFamily}'; src: url(data:font/truetype;base64,${NEXA_REGULAR_TTF_BASE64}) format('truetype'); font-weight: 400; font-style: normal; }`,
      `@font-face { font-family: '${fontFamily}'; src: url(data:font/truetype;base64,${NEXA_BOLD_TTF_BASE64}) format('truetype'); font-weight: 700; font-style: normal; }`,
    ].join('\n')
  } catch {
    return ''
  }
}

async function loadNexaFont(doc: TJsPdfDoc): Promise<string> {
  try {
    const { NEXA_REGULAR_TTF_BASE64, NEXA_BOLD_TTF_BASE64 } = await import('./chatbot-pdf-fonts')

    doc.addFileToVFS('Nexa-Regular.ttf', NEXA_REGULAR_TTF_BASE64)
    doc.addFont('Nexa-Regular.ttf', 'Nexa', 'normal')
    doc.addFileToVFS('Nexa-Bold.ttf', NEXA_BOLD_TTF_BASE64)
    doc.addFont('Nexa-Bold.ttf', 'Nexa', 'bold')

    return 'Nexa'
  } catch {
    return 'helvetica'
  }
}

function pointAlong(from: [number, number], to: [number, number], dist: number): [number, number] {
  const dx = to[0] - from[0]
  const dy = to[1] - from[1]
  const len = Math.hypot(dx, dy)
  return [from[0] + (dx / len) * dist, from[1] + (dy / len) * dist]
}

type TDrawRoundedFilledTriangleParams = {
  doc: TJsPdfDoc
  apex: [number, number]
  bottomLeft: [number, number]
  bottomRight: [number, number]
  r: number
}

function drawRoundedFilledTriangle({
  doc,
  apex,
  bottomLeft,
  bottomRight,
  r,
}: TDrawRoundedFilledTriangleParams): void {
  const p1 = pointAlong(apex, bottomLeft, r)
  const p2 = pointAlong(bottomLeft, apex, r)
  const p3 = pointAlong(bottomLeft, bottomRight, r)
  const p4 = pointAlong(bottomRight, bottomLeft, r)
  const p5 = pointAlong(bottomRight, apex, r)
  const p6 = pointAlong(apex, bottomRight, r)
  const [ax, ay] = apex
  const [bx, by] = bottomLeft
  const [cx, cy] = bottomRight

  doc.lines(
    [
      [p2[0] - p1[0], p2[1] - p1[1]],
      [bx - p2[0], by - p2[1], bx - p2[0], by - p2[1], p3[0] - p2[0], p3[1] - p2[1]],
      [p4[0] - p3[0], p4[1] - p3[1]],
      [cx - p4[0], cy - p4[1], cx - p4[0], cy - p4[1], p5[0] - p4[0], p5[1] - p4[1]],
      [p6[0] - p5[0], p6[1] - p5[1]],
      [ax - p6[0], ay - p6[1], ax - p6[0], ay - p6[1], p1[0] - p6[0], p1[1] - p6[1]],
    ],
    p1[0],
    p1[1],
    [1, 1],
    'F',
    false,
  )
}

interface TDrawBadgeParams {
  doc: TJsPdfDoc
  text: string
  x: number
  y: number
  borderColor: [number, number, number]
  bgColor: [number, number, number]
  textColor: [number, number, number]
  showAlert?: boolean
}
function drawBadge({
  doc,
  text,
  x,
  y,
  borderColor,
  bgColor,
  textColor,
  showAlert,
}: TDrawBadgeParams): number {
  const textWidth = doc.getTextWidth(text)
  const badgePadding = 4
  const badgeHeight = 12
  const triangleSize = 6
  const gap = 2
  const iconSpace = showAlert ? triangleSize + gap : 0
  const badgeWidth = badgePadding + iconSpace + textWidth + badgePadding
  const radius = 2

  doc.setDrawColor(...borderColor)
  doc.setFillColor(...bgColor)
  doc.roundedRect(x, y, badgeWidth, badgeHeight, radius, radius, 'FD')

  if (showAlert) {
    const triX = x + badgePadding
    const triCenterY = y + badgeHeight / 2
    const { fontName, fontStyle } = doc.getFont()
    const fontSize = doc.getFontSize()

    doc.setFillColor(...textColor)
    drawRoundedFilledTriangle({
      doc,
      apex: [triX + triangleSize / 2, triCenterY - triangleSize / 2],
      bottomLeft: [triX, triCenterY + triangleSize / 2],
      bottomRight: [triX + triangleSize, triCenterY + triangleSize / 2],
      r: 0.8,
    })
    doc.setFontSize(5)
    doc.setFont(fontName, 'bold')
    doc.setTextColor(255, 255, 255)
    doc.text('!', triX + triangleSize / 2, triCenterY + 2.2, { align: 'center' })

    doc.setFontSize(fontSize)
    doc.setFont(fontName, fontStyle)
  }

  doc.setTextColor(...textColor)
  doc.text(text, x + badgePadding + iconSpace, y + badgeHeight - 3.5)

  return badgeWidth
}

type TDrawHeaderParams = {
  doc: TJsPdfDoc
  chatbotName: string
  projectName: string
  followupName: string
  labels: TPdfLabels
  fontFamily: string
  logoDataUrl?: string | null
}

function drawHeader({
  doc,
  chatbotName,
  projectName,
  followupName,
  labels,
  fontFamily,
  logoDataUrl,
}: TDrawHeaderParams) {
  const pageWidth = doc.internal.pageSize.getWidth()
  const headerY = PAGE_MARGIN
  const centerY = headerY + HEADER_HEIGHT / 2

  let textStartX = PAGE_MARGIN
  if (logoDataUrl) {
    const logoHeight = 16
    const logoWidth = logoHeight * (LOGO_WIDTH / LOGO_HEIGHT)
    doc.addImage(logoDataUrl, 'PNG', PAGE_MARGIN, centerY - logoHeight / 2, logoWidth, logoHeight)
    textStartX = PAGE_MARGIN + logoWidth + 6
  }

  // Measure right-side text first to know how much space the name can occupy
  doc.setFontSize(8)
  doc.setFont(fontFamily, 'normal')
  const rightText = `${labels.establishmentLabel}: ${projectName}  |  ${labels.interventionLabel}: ${followupName}`
  const rightTextWidth = doc.getTextWidth(rightText)

  doc.setFontSize(11)
  const labelPrefix = `${labels.chatbotLabel}: `
  const labelPrefixWidth = doc.getTextWidth(labelPrefix)
  const nameStartX = textStartX + labelPrefixWidth
  const availableNameWidth = pageWidth - PAGE_MARGIN - rightTextWidth - 12 - nameStartX

  let displayName = chatbotName
  if (doc.getTextWidth(chatbotName) > availableNameWidth) {
    while (displayName.length > 1 && doc.getTextWidth(displayName + '…') > availableNameWidth) {
      displayName = displayName.slice(0, -1)
    }
    displayName += '…'
  }

  doc.setFont(fontFamily, 'normal')
  doc.setTextColor(60)
  doc.text(labelPrefix, textStartX, centerY, { baseline: 'middle' })
  doc.setFont(fontFamily, 'bold')
  doc.setTextColor(0)
  doc.text(displayName, nameStartX, centerY, { baseline: 'middle' })

  doc.setFontSize(8)
  doc.setFont(fontFamily, 'normal')
  doc.setTextColor(100)
  doc.text(rightText, pageWidth - PAGE_MARGIN, centerY, { align: 'right', baseline: 'middle' })

  doc.setDrawColor(200)
  doc.setLineWidth(0.5)
  doc.line(PAGE_MARGIN, headerY + HEADER_HEIGHT, pageWidth - PAGE_MARGIN, headerY + HEADER_HEIGHT)
}

type TDrawFooterParams = {
  doc: TJsPdfDoc
  pageIndex: number
  totalPages: number
  labels: TPdfLabels
  fontFamily: string
  exportDate: string
}

function drawFooter({ doc, pageIndex, totalPages, labels, fontFamily, exportDate }: TDrawFooterParams) {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const footerY = pageHeight - PAGE_MARGIN - FOOTER_HEIGHT

  doc.setDrawColor(200)
  doc.setLineWidth(0.5)
  doc.line(PAGE_MARGIN, footerY, pageWidth - PAGE_MARGIN, footerY)

  const textY = footerY + 14
  doc.setFontSize(7)
  doc.setFont(fontFamily, 'normal')
  doc.setTextColor(100)

  const dateText = `${labels.exportDateLabel}: ${exportDate}`
  doc.text(dateText, PAGE_MARGIN, textY)

  let cursorX = PAGE_MARGIN + doc.getTextWidth(dateText) + 8
  doc.text('|', cursorX, textY)
  cursorX += doc.getTextWidth('|') + 8

  const legendText = `${labels.legendLabel}:`
  doc.text(legendText, cursorX, textY)
  cursorX += doc.getTextWidth(legendText) + 6

  const badgeY = textY - 9

  doc.setFontSize(6)
  const alertWidth = drawBadge({
    doc,
    text: labels.alertResponseLabel,
    x: cursorX,
    y: badgeY,
    borderColor: [190, 24, 93],
    bgColor: [252, 231, 243],
    textColor: [190, 24, 93],
    showAlert: true,
  })
  cursorX += alertWidth + 4

  const normalWidth = drawBadge({
    doc,
    text: labels.normalResponseLabel,
    x: cursorX,
    y: badgeY,
    borderColor: [156, 163, 175],
    bgColor: [255, 255, 255],
    textColor: [31, 41, 55],
  })
  cursorX += normalWidth + 4

  drawBadge({
    doc,
    text: labels.contentLabel,
    x: cursorX,
    y: badgeY,
    borderColor: [156, 163, 175],
    bgColor: [249, 250, 251],
    textColor: [75, 85, 99],
  })

  doc.setFontSize(8)
  doc.setTextColor(100)
  doc.text(labels.pageLabel(pageIndex + 1, totalPages), pageWidth - PAGE_MARGIN, textY, {
    align: 'right',
  })
}

type TBuildPdfParams = {
  pageImages: string[]
  chatbotName: string
  projectName: string
  followupName: string
  labels: TPdfLabels
  jsPDFClass: typeof import('jspdf').jsPDF
  orientation: TPdfOrientation
  imageWidth: number
  imageHeight: number
}

async function buildPdf({
  pageImages,
  chatbotName,
  projectName,
  followupName,
  labels,
  jsPDFClass,
  orientation,
  imageWidth,
  imageHeight,
}: TBuildPdfParams) {
  const doc = new jsPDFClass({ orientation, unit: 'pt', format: 'a4' })
  const [fontFamily, logoDataUrl] = await Promise.all([loadNexaFont(doc), getLogoDataUrl()])
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()

  const contentTop = PAGE_MARGIN + HEADER_HEIGHT + 4
  const contentWidth = pageWidth - PAGE_MARGIN * 2
  const contentHeight = pageHeight - PAGE_MARGIN * 2 - HEADER_HEIGHT - FOOTER_HEIGHT - 8

  const imgAspect = imageWidth / imageHeight
  const areaAspect = contentWidth / contentHeight
  let drawWidth: number
  let drawHeight: number
  if (imgAspect > areaAspect) {
    drawWidth = contentWidth
    drawHeight = contentWidth / imgAspect
  } else {
    drawHeight = contentHeight
    drawWidth = contentHeight * imgAspect
  }
  const drawX = PAGE_MARGIN + (contentWidth - drawWidth) / 2
  const drawY = contentTop + (contentHeight - drawHeight) / 2

  const totalPages = pageImages.length
  const now = dayjs()
  const exportDateDisplay = now.format('DD/MM/YYYY HH:mm')
  const exportDateFile = now.format('YYYY-MM-DD')

  for (const [index, pageImage] of pageImages.entries()) {
    if (index > 0) doc.addPage()

    drawHeader({ doc, chatbotName, projectName, followupName, labels, fontFamily, logoDataUrl })
    doc.addImage(pageImage, 'PNG', drawX, drawY, drawWidth, drawHeight)
    drawFooter({ doc, pageIndex: index, totalPages, labels, fontFamily, exportDate: exportDateDisplay })
  }

  doc.save(`${projectName} - Curecall - ${chatbotName} - ${exportDateFile}.pdf`)
}

type TCaptureToPngParams = {
  viewportElement: HTMLElement
  viewport: { x: number; y: number; zoom: number }
  fontEmbedCSS: string
  imageWidth: number
  imageHeight: number
}

async function captureToPng({
  viewportElement,
  viewport,
  fontEmbedCSS,
  imageWidth,
  imageHeight,
}: TCaptureToPngParams): Promise<string> {
  return toPng(viewportElement, {
    pixelRatio: 2,
    backgroundColor: '#ffffff',
    fontEmbedCSS,
    width: imageWidth,
    height: imageHeight,
    style: {
      width: `${imageWidth}px`,
      height: `${imageHeight}px`,
      transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
    },
  })
}

export function computePageStarts(
  nodes: Node[],
  nodesBounds: { y: number; height: number },
  pageFlowHeight: number,
): number[] {
  const flowTop = nodesBounds.y
  const flowBottom = flowTop + nodesBounds.height

  if (nodesBounds.height <= pageFlowHeight) return [flowTop]

  const nodeIntervals = nodes
    .map((node) => ({
      top: node.position.y,
      bottom: node.position.y + (node.measured?.height ?? DEFAULT_NODE_HEIGHT),
    }))
    .sort((a, b) => a.top - b.top)

  const maxPages = Math.ceil(nodesBounds.height / pageFlowHeight) + 1
  const pageStarts: number[] = [flowTop]
  let currentStart = flowTop

  for (let i = 0; i < maxPages; i++) {
    const pageEnd = currentStart + pageFlowHeight
    if (pageEnd >= flowBottom) break

    let nextStart = pageEnd

    for (const interval of nodeIntervals) {
      if (interval.top >= pageEnd) break
      if (interval.top < pageEnd && interval.bottom > pageEnd) {
        nextStart = Math.min(nextStart, interval.top - NODE_PADDING)
        break
      }
    }

    if (nextStart <= currentStart) {
      nextStart = currentStart + pageFlowHeight
    }

    currentStart = nextStart
    pageStarts.push(currentStart)
  }

  return pageStarts
}

type TCapturePagesParams = {
  viewportElement: HTMLElement
  nodes: Node[]
  nodesBounds: { x: number; y: number; width: number; height: number }
  fontEmbedCSS: string
  imageWidth: number
  imageHeight: number
  zoomOverride?: number
}

async function capturePages({
  viewportElement,
  nodes,
  nodesBounds,
  fontEmbedCSS,
  imageWidth,
  imageHeight,
  zoomOverride,
}: TCapturePagesParams): Promise<string[]> {
  const zoomFitWidth = imageWidth / nodesBounds.width
  const zoomFitHeight = imageHeight / nodesBounds.height
  const autoZoom = Math.max(MIN_PREVIEW_ZOOM, Math.min(MAX_PREVIEW_ZOOM, zoomFitWidth, zoomFitHeight))
  const zoom = zoomOverride !== undefined ? Math.min(zoomOverride, zoomFitWidth) : autoZoom
  const pageFlowHeight = imageHeight / zoom
  const pageStarts = computePageStarts(nodes, nodesBounds, pageFlowHeight)
  const pages: string[] = []

  for (const pageY of pageStarts) {
    const pageBounds = {
      x: nodesBounds.x,
      y: pageY,
      width: nodesBounds.width,
      height: pageFlowHeight,
    }

    const viewport = getViewportForBounds(pageBounds, imageWidth, imageHeight, zoom, zoom, 0)
    pages.push(await captureToPng({ viewportElement, viewport, fontEmbedCSS, imageWidth, imageHeight }))
  }

  return pages
}

function hierarchicalBaryCenter(neighborIds: string[], indexMap: Map<string, number>): number {
  const indices = neighborIds.map((id) => indexMap.get(id)).filter((v): v is number => v !== undefined)
  if (indices.length === 0) return Infinity
  return indices.reduce((sum, v) => sum + v, 0) / indices.length
}

function compareBaryCenters(a: number, b: number): number {
  if (!isFinite(a) && !isFinite(b)) return 0
  if (!isFinite(a)) return 1
  if (!isFinite(b)) return -1
  return a - b
}

function getHandleRank(sourceHandle: string | null | undefined): number {
  if (sourceHandle === 'yes') return 0
  if (sourceHandle === 'no') return 1
  if (sourceHandle?.startsWith('range-')) return parseInt(sourceHandle.slice('range-'.length), 10)
  return 0
}

function computeHierarchicalLayout(nodes: Node[], edges: Edge[]): Node[] {
  if (nodes.length === 0) return nodes

  const nodeMap = new Map(nodes.map((node) => [node.id, node]))
  const childrenOf = new Map<string, string[]>(nodes.map((node) => [node.id, []]))
  const parentsOf = new Map<string, string[]>(nodes.map((node) => [node.id, []]))

  // handleRank encodes the visual left→right order of source handles (yes=0, no=1, range-N=N)
  const handleRank = new Map<string, Map<string, number>>()
  for (const node of nodes) handleRank.set(node.id, new Map())
  for (const edge of edges) {
    childrenOf.get(edge.source)?.push(edge.target)
    parentsOf.get(edge.target)?.push(edge.source)
    const parentMap = handleRank.get(edge.source)!
    if (!parentMap.has(edge.target)) {
      parentMap.set(edge.target, getHandleRank(edge.sourceHandle))
    }
  }

  // Longest-path layer assignment via Kahn topological DP
  const layers = new Map<string, number>(nodes.map((node) => [node.id, 0]))
  const inDegree = new Map<string, number>(nodes.map((node) => [node.id, 0]))
  for (const childs of childrenOf.values()) {
    for (const child of childs) inDegree.set(child, (inDegree.get(child) ?? 0) + 1)
  }

  // Seed with roots, START_NODE_ID first
  const topoQueue = nodes
    .filter((node) => inDegree.get(node.id) === 0)
    .map((node) => node.id)
    .sort((a) => (a === START_NODE_ID ? -1 : 0))

  while (topoQueue.length > 0) {
    const current = topoQueue.shift()!
    const currentLayer = layers.get(current) ?? 0
    for (const child of childrenOf.get(current) ?? []) {
      if ((layers.get(child) ?? 0) < currentLayer + 1) layers.set(child, currentLayer + 1)
      const deg = (inDegree.get(child) ?? 0) - 1
      inDegree.set(child, deg)
      if (deg === 0) topoQueue.push(child)
    }
  }

  const maxLayer = Math.max(...layers.values())

  // Group by layer, initially sorted by original X
  const layerGroups = new Map<number, string[]>()
  for (let i = 0; i <= maxLayer; i++) layerGroups.set(i, [])
  for (const [id, layer] of layers) layerGroups.get(layer)!.push(id)
  for (const group of layerGroups.values()) {
    group.sort((a, b) => (nodeMap.get(a)?.position.x ?? 0) - (nodeMap.get(b)?.position.x ?? 0))
  }

  // Minimize crossings: 3 passes of barycenter (down then up)
  for (let pass = 0; pass < 3; pass++) {
    // DOWN sweep: sort each layer by the position of its parents in the layer above
    for (let layer = 1; layer <= maxLayer; layer++) {
      const group = layerGroups.get(layer)!
      const prevOrder = new Map(layerGroups.get(layer - 1)!.map((id, i) => [id, i]))
      group.sort((a, b) => {
        const cmp = compareBaryCenters(
          hierarchicalBaryCenter(parentsOf.get(a) ?? [], prevOrder),
          hierarchicalBaryCenter(parentsOf.get(b) ?? [], prevOrder),
        )
        if (cmp !== 0) return cmp
        // Tie-break by handle rank (yes=0, no=1, range-N=N) to match visual badge order
        for (const parent of parentsOf.get(a) ?? []) {
          const rankA = handleRank.get(parent)?.get(a)
          const rankB = handleRank.get(parent)?.get(b)
          if (rankA !== undefined && rankB !== undefined) return rankA - rankB
        }
        return 0
      })
    }
    // UP sweep: sort each layer by the position of its children in the layer below.
    // Childless nodes keep their current index to avoid being displaced by Infinity barycenter.
    for (let layer = maxLayer - 1; layer >= 0; layer--) {
      const group = layerGroups.get(layer)!
      const currentIndex = new Map(group.map((id, i) => [id, i]))
      const nextOrder = new Map(layerGroups.get(layer + 1)!.map((id, i) => [id, i]))
      group.sort((a, b) =>
        compareBaryCenters(
          childrenOf.get(a)?.length
            ? hierarchicalBaryCenter(childrenOf.get(a)!, nextOrder)
            : (currentIndex.get(a) ?? 0),
          childrenOf.get(b)?.length
            ? hierarchicalBaryCenter(childrenOf.get(b)!, nextOrder)
            : (currentIndex.get(b) ?? 0),
        ),
      )
    }
  }

  // Assign X: pack nodes within each layer, then center the layer at x=0
  const xPos = new Map<string, number>()
  for (const group of layerGroups.values()) {
    let x = 0
    for (const id of group) {
      xPos.set(id, x)
      x += (nodeMap.get(id)?.measured?.width ?? DEFAULT_NODE_WIDTH) + HIER_H_GAP
    }
    const totalWidth = x - HIER_H_GAP
    const centerOffset = totalWidth / 2
    for (const id of group) xPos.set(id, (xPos.get(id) ?? 0) - centerOffset)
  }

  // Assign Y: cumulative height of preceding layers
  const layerY = new Map<number, number>()
  let y = 0
  for (let layer = 0; layer <= maxLayer; layer++) {
    layerY.set(layer, y)
    const group = layerGroups.get(layer) ?? []
    const maxH = Math.max(...group.map((id) => nodeMap.get(id)?.measured?.height ?? DEFAULT_NODE_HEIGHT))
    y += maxH + HIER_V_GAP
  }

  return nodes.map((node) => ({
    ...node,
    position: {
      x: xPos.get(node.id) ?? node.position.x,
      y: layerY.get(layers.get(node.id) ?? 0) ?? node.position.y,
    },
  }))
}

export async function exportChatbotPdfPreview({
  flowContainer,
  nodes,
  edges,
  chatbotName,
  projectName,
  followupName,
  labels,
  applyLayout,
  restoreLayout,
  zoomOverride,
  orientation,
  autoLayout,
}: TPreviewPdfExportParams): Promise<void> {
  const viewportElement = flowContainer.querySelector('.react-flow__viewport') as HTMLElement | null
  if (!viewportElement) return

  const { imageWidth, imageHeight } = getPdfDimensions(orientation)
  const compactedNodes = autoLayout ? computeHierarchicalLayout(nodes, edges) : nodes
  const nodesBounds = getNodesBounds(compactedNodes)
  const fontEmbedCSS = await buildFontEmbedCSS()
  const pageImages: string[] = []

  try {
    await applyLayout(compactedNodes, edges)
    const originalEdgeStyles = inlineEdgeStyles(viewportElement)
    pageImages.push(
      ...(await capturePages({
        viewportElement,
        nodes: compactedNodes,
        nodesBounds,
        fontEmbedCSS,
        imageWidth,
        imageHeight,
        zoomOverride,
      })),
    )
    restoreEdgeStyles(originalEdgeStyles)
  } finally {
    restoreLayout()
  }

  const { jsPDF } = await import('jspdf')
  await buildPdf({
    pageImages,
    chatbotName,
    projectName,
    followupName,
    labels,
    jsPDFClass: jsPDF,
    orientation,
    imageWidth,
    imageHeight,
  })
}

export function sortNodesInReadingOrder(nodes: Node[], edges: Edge[]): Node[] {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]))
  const adjacency = new Map<string, string[]>(nodes.map((node) => [node.id, []]))

  for (const edge of edges) {
    adjacency.get(edge.source)?.push(edge.target)
  }

  const visited = new Set<string>()
  const sorted: Node[] = []

  const queue: string[] = [START_NODE_ID]
  visited.add(START_NODE_ID)

  while (queue.length > 0) {
    const currentId = queue.shift()!
    const node = nodeMap.get(currentId)
    if (node) sorted.push(node)

    const children = adjacency.get(currentId) ?? []
    const sortedChildren = children
      .filter((childId) => !visited.has(childId))
      .sort((childIdA, childIdB) => {
        const nodeA = nodeMap.get(childIdA)
        const nodeB = nodeMap.get(childIdB)
        if (!nodeA || !nodeB) return 0
        if (nodeA.position.y !== nodeB.position.y) return nodeA.position.y - nodeB.position.y
        return nodeA.position.x - nodeB.position.x
      })

    for (const childId of sortedChildren) {
      visited.add(childId)
      queue.push(childId)
    }
  }

  for (const node of nodes) {
    if (!visited.has(node.id)) {
      sorted.push(node)
    }
  }

  return sorted
}

function positionNodesInGrid(nodes: Node[], cols: number): Node[] {
  return nodes.map((node, index) => ({
    ...node,
    position: {
      x: (index % cols) * GRID_CELL_WIDTH,
      y: Math.floor(index / cols) * GRID_CELL_HEIGHT,
    },
  }))
}

function getEdgesForPage(edges: Edge[], pageNodeIds: Set<string>): Edge[] {
  return edges
    .filter((edge) => pageNodeIds.has(edge.source) && pageNodeIds.has(edge.target))
    .map((edge) => ({ ...edge, type: 'default', markerEnd: { type: MarkerType.ArrowClosed } }))
}

type TCaptureGridPageParams = {
  viewportElement: HTMLElement
  fontEmbedCSS: string
  imageWidth: number
  imageHeight: number
  gridCols: number
  gridRows: number
}

async function captureGridPage({
  viewportElement,
  fontEmbedCSS,
  imageWidth,
  imageHeight,
  gridCols,
  gridRows,
}: TCaptureGridPageParams): Promise<string> {
  // Use fixed grid bounds so every page has the same viewport regardless of node content heights
  const gridBounds = { x: 0, y: 0, width: gridCols * GRID_CELL_WIDTH, height: gridRows * GRID_CELL_HEIGHT }
  const viewport = getViewportForBounds(gridBounds, imageWidth, imageHeight, 0.5, 2, 0.05)

  const originalEdgeStyles = inlineEdgeStyles(viewportElement)
  const image = await captureToPng({ viewportElement, viewport, fontEmbedCSS, imageWidth, imageHeight })
  restoreEdgeStyles(originalEdgeStyles)

  return image
}

export async function exportChatbotPdfGrid({
  flowContainer,
  nodes,
  edges,
  chatbotName,
  projectName,
  followupName,
  labels,
  applyLayout,
  restoreLayout,
  orientation,
}: TGridPdfExportParams): Promise<void> {
  const viewportElement = flowContainer.querySelector('.react-flow__viewport') as HTMLElement | null
  if (!viewportElement) return

  const { imageWidth, imageHeight, gridCols, gridRows } = getPdfDimensions(orientation)
  const nodesPerPage = gridCols * gridRows
  const sorted = sortNodesInReadingOrder(nodes, edges)
  const totalPages = Math.max(1, Math.ceil(sorted.length / nodesPerPage))
  const fontEmbedCSS = await buildFontEmbedCSS()
  const pageImages: string[] = []

  try {
    for (let page = 0; page < totalPages; page++) {
      const pageNodes = sorted.slice(page * nodesPerPage, (page + 1) * nodesPerPage)
      const gridNodes = positionNodesInGrid(pageNodes, gridCols)
      const pageNodeIds = new Set(pageNodes.map((node) => node.id))
      const pageEdges = getEdgesForPage(edges, pageNodeIds)

      await applyLayout(gridNodes, pageEdges)
      const image = await captureGridPage({
        viewportElement,
        fontEmbedCSS,
        imageWidth,
        imageHeight,
        gridCols,
        gridRows,
      })
      pageImages.push(image)
    }
  } finally {
    restoreLayout()
  }

  const { jsPDF } = await import('jspdf')
  await buildPdf({
    pageImages,
    chatbotName,
    projectName,
    followupName,
    labels,
    jsPDFClass: jsPDF,
    orientation,
    imageWidth,
    imageHeight,
  })
}
